import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { AuthGuard, EmailGuard } from "../App";
import { FaSearch,FaSpinner, FaDownload, FaPlus, FaEdit, FaTrash, FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaFileCsv } from 'react-icons/fa';
import { StandardURL } from "../App";


// --- Custom Modal Component (Replaces generic modal div for better styling) ---

const CustomModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex justify-center items-center p-4 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
                <div className="flex justify-between items-center border-b p-5">
                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition duration-150">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>
                <div className="p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

// --- Helper Functions ---

const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', // Assuming Kenyan Shillings, can be changed
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const TransactionsViewPage = () => {
    // --- State Initialization (Functionality preserved) ---
    const [propertyId, setPropertyId] = useState("");
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [amount, setAmount] = useState("");
    const [transactionIds, setTransactionIds] = useState("");
    const [runningBalance, setRunningBalance] = useState("");
    const [channel, setChannel] = useState("");
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [postModalOpen, setPostModalOpen] = useState(false);
    const [transactionToUpdate, setTransactionToUpdate] = useState(null);

    // --- Pagination States (New) ---
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionsPerPage] = useState(10); // 10 items per page

    // --- Core Functions (Functionality preserved) ---

    const fetchTransactions = () => {
        if (!propertyId.trim()) {
             toast.warn("Please enter a Property ID to search.");
             return;
        }

        setIsLoading(true);
        setCurrentPage(1); // Reset to first page on new search
        const token = localStorage.getItem("token");
        axios
            .get(
                `${StandardURL}/iNyumba/alltransactionsview/${propertyId.trim()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                setTransactions(response.data.response || []);
                if (!response.data.response || response.data.response.length === 0) {
                     toast.info("No transactions found for this Property ID.");
                } else {
                     toast.success(`${response.data.response.length} transactions fetched.`);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching transactions:", error);
                toast.error(
                    error.response?.data?.response || "Error fetching transactions"
                );
                setTransactions([]); // Clear transactions on error
                setIsLoading(false);
            });
    };

    const handleDeleteTransaction = (transactionId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this transaction?");
        if (!confirmDelete) return;

        const token = localStorage.getItem("token");
        axios
            .delete(
                `${StandardURL}/iNyumba/transactionscontroller/deletetransaction/${transactionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then(() => {
                toast.success("Transaction deleted successfully.");
                fetchTransactions(); // Re-fetch to update the list
            })
            .catch((error) => {
                console.error("Error deleting transaction:", error);
                toast.error("Failed to delete transaction.");
            });
    };

    const handleUpdateTransaction = (transactionId) => {
        const token = localStorage.getItem("token");
        const data = {
            amount: amount, // State values used as is (null allowed)
            runningbalance: runningBalance, // State values used as is (null allowed)
        };

        axios
            .patch(
                `${StandardURL}/iNyumba/transactionscontroller/updatetransaction/${transactionId}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then(() => {
                toast.success("Transaction updated successfully.");
                fetchTransactions();
                setUpdateModalOpen(false);
                setAmount(null); // Functionality preserved
                setRunningBalance(null); // Functionality preserved
            })
            .catch((error) => {
                console.error("Error updating transaction:", error);
                toast.error("Failed to update transaction.");
                setUpdateModalOpen(false);
                setAmount(null); // Functionality preserved
                setRunningBalance(null); // Functionality preserved
            });
    };

    const handlePostTransaction = () => {
        if (!propertyId.trim()) {
            toast.error("Please enter a Property ID in the search bar before posting a transaction.");
            return;
        }
        if (!channel || !amount || !runningBalance) {
             toast.error("Please fill in all required fields.");
             return;
        }

        const token = localStorage.getItem("token");
        const data = {
            reference: channel,
            amount: amount,
            runningbalance: runningBalance,
            transactionid: transactionIds,
        };

        axios
            .post(
                `${StandardURL}/iNyumba/transactionscontroller/posttransaction/${propertyId.trim()}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then(() => {
                toast.success("Transaction posted successfully.");
                fetchTransactions();
                setPostModalOpen(false);
                setChannel(null); // Functionality preserved
                setAmount(null); // Functionality preserved
                setRunningBalance(null); // Functionality preserved
                setTransactionIds(null); // Functionality preserved
            })
            .catch((error) => {
                console.error("Error posting transaction:", error);
                toast.error("Failed to post transaction.");
                setPostModalOpen(false);
                setChannel(null); // Functionality preserved
                setAmount(null); // Functionality preserved
                setRunningBalance(null); // Functionality preserved
                setTransactionIds(null); // Functionality preserved
            });
    };

    const handleDownloadReport = () => {
        if (!propertyId.trim()) {
            toast.warn("Please enter a Property ID to download the report.");
            return;
        }

        const token = localStorage.getItem("token");
        axios
            .get(
                `${StandardURL}/iNyumba/reportsdownloadtransactions/${propertyId.trim()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    responseType: "blob",
                }
            )
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `transactions_report_${propertyId}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("Report download started.");
            })
            .catch((error) => {
                console.error("Error downloading report:", error);
                toast.error("Failed to download report. Check if transactions exist for the ID.");
            });
    };

    // --- Pagination Logic (New) ---

    // Get current transactions
    const indexOfLastTransaction = currentPage * transactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
    const currentTransactions = transactions.slice(indexOfFirstTransaction, indexOfLastTransaction);

    // Calculate total pages
    const totalPages = Math.ceil(transactions.length / transactionsPerPage);

    // Change page
    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // --- JSX Rendering ---

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                
                    <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-6 lg:p-8">
                        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-6">
                            <FaMoneyBillWave className="mr-3 text-blue-600" /> Property Transactions
                        </h1>

                        {/* Search & Action Bar */}
                        <div className="flex flex-col sm:flex-row gap-3 mb-8 p-4 bg-gray-100 rounded-lg shadow-inner">
                            <div className="flex flex-grow gap-2">
                                <input
                                    type="text"
                                    value={propertyId}
                                    onChange={(e) => setPropertyId(e.target.value)}
                                    placeholder="Enter Property ID to search (e.g., A101)"
                                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition duration-150"
                                />
                                <button
                                    onClick={fetchTransactions}
                                    className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 disabled:opacity-50"
                                    disabled={isLoading}
                                >
                                    {isLoading ? <FaSpinner className="animate-spin mr-2" /> : <FaSearch className="mr-2" />}
                                    Search
                                </button>
                            </div>
                            
                            <div className="flex gap-2 sm:ml-4">
                                <button
                                    onClick={handleDownloadReport}
                                    className="flex items-center bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition duration-200 disabled:opacity-50"
                                    disabled={!propertyId.trim() || transactions.length === 0}
                                >
                                    <FaFileCsv className="mr-2" /> Download CSV
                                </button>
                                <button
                                    onClick={() => setPostModalOpen(true)}
                                    className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition duration-200"
                                >
                                    <FaPlus className="mr-2" /> Add Manual
                                </button>
                            </div>
                        </div>

                        {/* Loading/Empty State */}
                        {isLoading ? (
                            <div className="flex justify-center p-12">
                                <Oval height={40} width={40} color="#3b82f6" secondaryColor="#93c5fd" ariaLabel="loading-indicator" />
                                <p className="ml-3 text-lg text-blue-600">Loading transactions...</p>
                            </div>
                        ) : transactions.length === 0 && propertyId.trim() ? (
                            <div className="p-12 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-xl">
                                <FaMoneyBillWave className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                                <p className="text-xl font-medium">No Transactions Found</p>
                                <p>Please check the Property ID or add a new transaction.</p>
                            </div>
                        ) : transactions.length > 0 && (
                            <>
                                {/* Transactions Table */}
                                <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Txn ID</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prop ID</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Running Balance</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentTransactions.map((transaction, index) => (
                                                <tr key={transaction.transaction_id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(transaction.created_at).toLocaleDateString()}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">{transaction.transaction_id}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{transaction.propertyid}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{transaction.reference}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-green-700">{formatCurrency(transaction.amount)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-red-700">{formatCurrency(transaction.running_balance)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                        <button
                                                            onClick={() => {
                                                                setTransactionToUpdate(transaction);
                                                                // Functionality preserved: setting state to existing values
                                                                setAmount(transaction.amount); 
                                                                setRunningBalance(transaction.running_balance); 
                                                                setUpdateModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded-full hover:bg-blue-100 transition"
                                                            title="Update Transaction"
                                                        >
                                                            <FaEdit className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteTransaction(transaction.transaction_id)}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition"
                                                            title="Delete Transaction"
                                                        >
                                                            <FaTrash className="w-5 h-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                <div className="mt-6 flex justify-between items-center">
                                    <div className="text-sm text-gray-600">
                                        Showing {indexOfFirstTransaction + 1} to {Math.min(indexOfLastTransaction, transactions.length)} of {transactions.length} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition"
                                        >
                                            <FaChevronLeft className="w-4 h-4" />
                                        </button>
                                        <span className="text-sm font-semibold text-gray-700">Page {currentPage} of {totalPages}</span>
                                        <button
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition"
                                        >
                                            <FaChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Post Transaction Modal (Using CustomModal) */}
                <CustomModal
                    isOpen={postModalOpen}
                    onClose={() => setPostModalOpen(false)}
                    title="Post New Transaction"
                >
                    <div className="space-y-4">
                        <input
                            type="text"
                            value={channel || ""}
                            onChange={(e) => setChannel(e.target.value)}
                            placeholder="Reference/Channel (e.g., M-Pesa, Bank)"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                            type="text"
                            value={transactionIds || ""}
                            onChange={(e) => setTransactionIds(e.target.value)}
                            placeholder="Transaction ID (Optional)"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                            type="number"
                            value={amount || ""}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Amount (e.g., 15000)"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                            type="number"
                            value={runningBalance || ""}
                            onChange={(e) => setRunningBalance(e.target.value)}
                            placeholder="Running Balance After Transaction"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={handlePostTransaction}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 disabled:opacity-50"
                            disabled={!channel || !amount || !runningBalance}
                        >
                            <FaPlus className="mr-2 inline" /> Add Transaction
                        </button>
                        <button
                            onClick={() => { setPostModalOpen(false); setChannel(null); setAmount(null); setRunningBalance(null); setTransactionIds(null); }}
                            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                        >
                            Close
                        </button>
                    </div>
                </CustomModal>

                {/* Update Transaction Modal (Using CustomModal) */}
                <CustomModal
                    isOpen={updateModalOpen}
                    onClose={() => setUpdateModalOpen(false)}
                    title={`Update Transaction: ${transactionToUpdate?.transaction_id || ''}`}
                >
                    <div className="space-y-4">
                        <input
                            type="number"
                            value={amount || ""}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Enter New Amount"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                            type="number"
                            value={runningBalance || ""}
                            onChange={(e) => setRunningBalance(e.target.value)}
                            placeholder="Enter New Running Balance"
                            className="border border-gray-300 rounded-md py-3 px-4 w-full focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={() =>
                                handleUpdateTransaction(
                                    transactionToUpdate.transaction_id
                                )
                            }
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-200 disabled:opacity-50"
                            disabled={!amount || !runningBalance}
                        >
                            <FaEdit className="mr-2 inline" /> Update Transaction
                        </button>
                        <button
                            onClick={() => setUpdateModalOpen(false)}
                            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                        >
                            Close
                        </button>
                    </div>
                </CustomModal>
            </AuthGuard>
        </EmailGuard>
    );
};

export default TransactionsViewPage;